import React, { useMemo } from "react";

import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { RequestStatus } from "@companion-core/shared/app/Components/Requests/RequestStatus";
import { getFamilyIcon } from "@companion-core/shared/app/Utils/myRequests";
import { RequestCardProps } from "./requestCard.model";
import { useRequestCard } from "./hooks/useRequestCard";
import "@companion-core/web/src/Assets/Styles/scss/myRequestsCard.scss";
dayjs.extend(localizedFormat);

export const RequestCard = ({ data, onClick, accessibilityLabel, path }: RequestCardProps) => {
  const { t } = useTranslation();
  const { detailTitle } = useRequestCard(data);
  const status = useMemo(
    () =>
      !data.has_digital_proposal && data.status === "recommended"
        ? "recommended-by-mail"
        : data.status,
    [data]
  );
  const titleId = `request-card__title-${data.id}`;
  const dateId = `request-card__date-${data.id}`;
  const statusId = `request-card__status-${data.id}`;
  const accessibilityLabelId = `request-card__accessibility-label-${data.id}`;

  const SrPause = () => <span className="request-card__sr-only">.</span>;

  return (
    <Link
      to={path ?? ""}
      className={`request-card ${data.status === "cancelled" ? "request-card--cancelled" : ""}`}
      onClick={() => onClick?.()}
      data-testid="request-card"
      aria-labelledby={`${titleId} ${dateId} ${statusId} ${accessibilityLabelId}`}>
      {accessibilityLabel && (
        <p hidden id={accessibilityLabelId}>
          {accessibilityLabel}
        </p>
      )}
      <div className="request-card__data">
        <div className="d-flex" data-testid="request-card__title">
          <FontIcon
            name={getFamilyIcon(data.family ?? "", data.sub_family)}
            size={24}
            className="body-text-5 margin-right-8 request-card__icon"
            aria-hidden={true}
          />
          <h2 className="body-text-5 margin-bottom-8 request-card__title" id={titleId}>
            {/* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */}
            <span dangerouslySetInnerHTML={{ __html: detailTitle }} />
            <SrPause />
          </h2>
        </div>
        <div
          id={dateId}
          className="body-text-8 request-card__date"
          data-testid="request-card__date"
          aria-label={t(
            data.end_date ? "request.my-requests.double-date" : "request.my-requests.single-date",
            {
              startDate: dayjs(data.start_date).format("LL"),
              endDate: data.end_date ? dayjs(data.end_date).format("LL") : undefined,
            }
          )}>
          <time
            data-testid="request-card__start-date"
            dateTime={dayjs(data.start_date).format("YYYY-MM-DD")}
            aria-label={dayjs(data.start_date).format("LL")}>
            {dayjs(data.start_date).format("L")}
          </time>
          {data.end_date && (
            <>
              <FontIcon name="JP-Right" aria-hidden={true} />
              <time
                data-testid="request-card__end-date"
                dateTime={dayjs(data.end_date).format("YYYY-MM-DD")}
                aria-label={dayjs(data.end_date).format("LL")}>
                {dayjs(data.end_date).format("L")}
              </time>
            </>
          )}
        </div>
        <div id={statusId}>
          <RequestStatus status={status} />
          <SrPause />
        </div>
      </div>
      <div className="request-card__placeholder"></div>
      <div className="request-card__arrow" data-testid="request-card__arrow" aria-hidden={true}>
        <FontIcon size={16} name="JP-Arrow-right-bold" />
      </div>
    </Link>
  );
};
